/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
  CROP_ASPECT_RATIO,
  type CropAspect,
  type CropHandle,
  type CropRect,
  type ImageRect,
  type ImageSize,
  MAX_OVERLAY_HEIGHT_RATIO,
  MAX_OVERLAY_WIDTH_RATIO,
  MIN_CROP_RATIO,
  MIN_OVERLAY_HEIGHT_RATIO,
  MIN_OVERLAY_WIDTH_RATIO,
  type OverlayBase,
} from './image-editor-types'

export const getCropPixelAspect = (aspect: CropAspect): number | null =>
  aspect === 'free' ? null : CROP_ASPECT_RATIO[aspect]

export const getOrientedSize = (naturalWidth: number, naturalHeight: number, rotation: number): ImageSize =>
  rotation % 180 === 0 ? { width: naturalWidth, height: naturalHeight } : { width: naturalHeight, height: naturalWidth }

export const getOrientationCssTransform = (rotation: number, flipX: boolean) => {
  const rotate = `rotate(${rotation}deg)`
  return flipX ? `scaleX(-1) ${rotate}` : rotate
}

export const getContainedRect = (
  naturalWidth: number,
  naturalHeight: number,
  containerWidth: number,
  containerHeight: number
): ImageRect => {
  if (naturalWidth <= 0 || naturalHeight <= 0 || containerWidth <= 0 || containerHeight <= 0) {
    return { left: 0, top: 0, width: 0, height: 0 }
  }

  const scale = Math.min(containerWidth / naturalWidth, containerHeight / naturalHeight)
  const width = naturalWidth * scale
  const height = naturalHeight * scale

  return {
    left: (containerWidth - width) / 2,
    top: (containerHeight - height) / 2,
    width,
    height,
  }
}

export const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

export const clampCrop = (crop: CropRect): CropRect => {
  const width = clamp(crop.width, MIN_CROP_RATIO, 1)
  const height = clamp(crop.height, MIN_CROP_RATIO, 1)
  return {
    width,
    height,
    x: clamp(crop.x, 0, 1 - width),
    y: clamp(crop.y, 0, 1 - height),
  }
}

export const moveCrop = (crop: CropRect, dx: number, dy: number): CropRect =>
  clampCrop({
    ...crop,
    x: crop.x + dx,
    y: crop.y + dy,
  })

export const fitCropToAspect = (
  crop: CropRect,
  pixelAspect: number,
  orientedWidth: number,
  orientedHeight: number
): CropRect => {
  if (orientedWidth <= 0 || orientedHeight <= 0 || pixelAspect <= 0) {
    return clampCrop(crop)
  }

  const maxWidth = orientedWidth
  const maxHeight = orientedHeight
  let widthPx = maxWidth
  let heightPx = widthPx / pixelAspect
  if (heightPx > maxHeight) {
    heightPx = maxHeight
    widthPx = heightPx * pixelAspect
  }

  const centerX = (crop.x + crop.width / 2) * orientedWidth
  const centerY = (crop.y + crop.height / 2) * orientedHeight
  let xPx = centerX - widthPx / 2
  let yPx = centerY - heightPx / 2
  xPx = clamp(xPx, 0, maxWidth - widthPx)
  yPx = clamp(yPx, 0, maxHeight - heightPx)

  return clampCrop({
    x: xPx / orientedWidth,
    y: yPx / orientedHeight,
    width: widthPx / orientedWidth,
    height: heightPx / orientedHeight,
  })
}

export const resizeCropFromCorner = (
  crop: CropRect,
  handle: CropHandle,
  pointerX: number,
  pointerY: number,
  pixelAspect: number | null,
  orientedWidth: number,
  orientedHeight: number
): CropRect => {
  if (orientedWidth <= 0 || orientedHeight <= 0) {
    return clampCrop(crop)
  }

  const anchors: Record<CropHandle, { x: number; y: number }> = {
    nw: { x: crop.x + crop.width, y: crop.y + crop.height },
    ne: { x: crop.x, y: crop.y + crop.height },
    sw: { x: crop.x + crop.width, y: crop.y },
    se: { x: crop.x, y: crop.y },
  }
  const anchor = anchors[handle]
  const pointerPxX = clamp(pointerX, 0, 1) * orientedWidth
  const pointerPxY = clamp(pointerY, 0, 1) * orientedHeight
  const anchorPxX = anchor.x * orientedWidth
  const anchorPxY = anchor.y * orientedHeight
  const minCropPx = Math.max(32, Math.min(orientedWidth, orientedHeight) * MIN_CROP_RATIO)

  const signX = pointerPxX <= anchorPxX ? -1 : 1
  const signY = pointerPxY <= anchorPxY ? -1 : 1
  const maxWidthPx = signX < 0 ? anchorPxX : orientedWidth - anchorPxX
  const maxHeightPx = signY < 0 ? anchorPxY : orientedHeight - anchorPxY

  let widthPx = Math.min(maxWidthPx, Math.max(minCropPx, Math.abs(pointerPxX - anchorPxX)))
  let heightPx = Math.min(maxHeightPx, Math.max(minCropPx, Math.abs(pointerPxY - anchorPxY)))

  if (pixelAspect && pixelAspect > 0) {
    const heightFromWidth = widthPx / pixelAspect
    if (heightFromWidth <= maxHeightPx && heightFromWidth >= minCropPx) {
      heightPx = heightFromWidth
    } else {
      heightPx = clamp(heightFromWidth, Math.min(minCropPx, maxHeightPx), maxHeightPx)
      widthPx = heightPx * pixelAspect
      if (widthPx > maxWidthPx) {
        widthPx = maxWidthPx
        heightPx = widthPx / pixelAspect
      }
    }
    widthPx = clamp(widthPx, Math.min(minCropPx, maxWidthPx), maxWidthPx)
    heightPx = widthPx / pixelAspect
    if (heightPx > maxHeightPx) {
      heightPx = maxHeightPx
      widthPx = heightPx * pixelAspect
    }
  }

  const xPx = signX < 0 ? anchorPxX - widthPx : anchorPxX
  const yPx = signY < 0 ? anchorPxY - heightPx : anchorPxY

  return clampCrop({
    x: xPx / orientedWidth,
    y: yPx / orientedHeight,
    width: widthPx / orientedWidth,
    height: heightPx / orientedHeight,
  })
}

export const rotateCropCw = (crop: CropRect): CropRect => {
  const corners = [
    { x: crop.x, y: crop.y },
    { x: crop.x + crop.width, y: crop.y },
    { x: crop.x, y: crop.y + crop.height },
    { x: crop.x + crop.width, y: crop.y + crop.height },
  ].map(({ x, y }) => ({ x: 1 - y, y: x }))

  const xs = corners.map((corner) => corner.x)
  const ys = corners.map((corner) => corner.y)
  const x = Math.min(...xs)
  const y = Math.min(...ys)

  return clampCrop({
    x,
    y,
    width: Math.max(...xs) - x,
    height: Math.max(...ys) - y,
  })
}

export const flipCropX = (crop: CropRect): CropRect =>
  clampCrop({
    ...crop,
    x: 1 - crop.x - crop.width,
  })

export const overlayToOriented = <T extends OverlayBase>(overlay: T, crop: CropRect): T => ({
  ...overlay,
  x: crop.x + overlay.x * crop.width,
  y: crop.y + overlay.y * crop.height,
  width: overlay.width * crop.width,
  height: overlay.height * crop.height,
})

export const isOverlayIntersectingUnitSquare = (overlay: OverlayBase) =>
  overlay.x + overlay.width > 0 && overlay.x < 1 && overlay.y + overlay.height > 0 && overlay.y < 1

export const orientedToOverlay = <T extends OverlayBase>(oriented: T, crop: CropRect): T | null => {
  if (crop.width <= 0 || crop.height <= 0) {
    return null
  }

  const mapped = {
    ...oriented,
    x: (oriented.x - crop.x) / crop.width,
    y: (oriented.y - crop.y) / crop.height,
    width: oriented.width / crop.width,
    height: oriented.height / crop.height,
  }

  return isOverlayIntersectingUnitSquare(mapped) ? mapped : null
}

export const rotateOverlayCw = <T extends OverlayBase>(overlay: T, spaceWidth: number, spaceHeight: number): T => {
  const centerX = overlay.x + overlay.width / 2
  const centerY = overlay.y + overlay.height / 2
  const nextCenterX = 1 - centerY
  const nextCenterY = centerX
  const widthPx = overlay.width * spaceWidth
  const heightPx = overlay.height * spaceHeight
  const nextWidth = spaceHeight > 0 ? widthPx / spaceHeight : overlay.height
  const nextHeight = spaceWidth > 0 ? heightPx / spaceWidth : overlay.width

  return {
    ...overlay,
    width: nextWidth,
    height: nextHeight,
    x: nextCenterX - nextWidth / 2,
    y: nextCenterY - nextHeight / 2,
    rotation: overlay.rotation + 90,
  }
}

export const flipOverlayX = <T extends OverlayBase>(overlay: T): T => ({
  ...overlay,
  x: 1 - overlay.x - overlay.width,
  rotation: -overlay.rotation,
})

export const remapOverlaysForCrop = <T extends OverlayBase>(
  overlays: T[],
  oldCrop: CropRect,
  nextCrop: CropRect
): T[] =>
  overlays.flatMap((overlay) => {
    const oriented = overlayToOriented(overlay, oldCrop)
    const mapped = orientedToOverlay(oriented, nextCrop)
    return mapped ? [mapped] : []
  })

export const remapOverlaysForRotateCw = <T extends OverlayBase>(
  overlays: T[],
  crop: CropRect,
  orientedWidth: number,
  orientedHeight: number
): { overlays: T[]; crop: CropRect } => {
  const nextCrop = rotateCropCw(crop)
  return {
    crop: nextCrop,
    overlays: overlays.flatMap((overlay) => {
      const oriented = overlayToOriented(overlay, crop)
      const rotated = rotateOverlayCw(oriented, orientedWidth, orientedHeight)
      const mapped = orientedToOverlay(rotated, nextCrop)
      return mapped ? [mapped] : []
    }),
  }
}

export const remapOverlaysForFlipX = <T extends OverlayBase>(
  overlays: T[],
  crop: CropRect
): { overlays: T[]; crop: CropRect } => {
  const nextCrop = flipCropX(crop)
  return {
    crop: nextCrop,
    overlays: overlays.flatMap((overlay) => {
      const oriented = overlayToOriented(overlay, crop)
      const flipped = flipOverlayX(oriented)
      const mapped = orientedToOverlay(flipped, nextCrop)
      return mapped ? [mapped] : []
    }),
  }
}

export const clampOverlay = <T extends OverlayBase>(overlay: T): T => {
  const width = clamp(overlay.width, MIN_OVERLAY_WIDTH_RATIO, MAX_OVERLAY_WIDTH_RATIO)
  const height = clamp(overlay.height, MIN_OVERLAY_HEIGHT_RATIO, MAX_OVERLAY_HEIGHT_RATIO)
  return {
    ...overlay,
    width,
    height,
    x: clamp(overlay.x, 0, 1 - width),
    y: clamp(overlay.y, 0, 1 - height),
  }
}

export const clientPointToNormalized = (
  clientX: number,
  clientY: number,
  stageBox: { left: number; top: number },
  imageRect: ImageRect
) => {
  if (imageRect.width <= 0 || imageRect.height <= 0) {
    return { x: 0, y: 0 }
  }

  return {
    x: (clientX - stageBox.left - imageRect.left) / imageRect.width,
    y: (clientY - stageBox.top - imageRect.top) / imageRect.height,
  }
}
